class PredictiveSearch extends SearchForm {
  constructor() {
    super();

    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.faqPageDocumentPromise = null;

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.addEventListener('click', this.onFaqClick.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();

    const newSearchTerm = this.getQuery();

    if (!newSearchTerm.length) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.close(true);
      return;
    }

    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;
    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
      return;
    }

    /*
     * Bestehende machsport-Anpassung:
     * Suchbegriff vor dem Absenden auf einen Shopify-kompatiblen Alias ändern.
     */
    const normalizedTerm = this.normalizeQuery(this.getQuery());
    const shopifySearchTerm = this.getShopifySearchTerm(normalizedTerm);

    if (shopifySearchTerm.length) {
      this.input.value = shopifySearchTerm;
    }
  }

  onFormReset(event) {
    super.onFormReset(event);

    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
      return;
    }

    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;

      case 'ArrowDown':
        this.switchOption('down');
        break;

      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      '[data-predictive-search-search-for-text]'
    );

    const currentButtonText = searchForTextElement?.innerText;

    if (!searchForTextElement || !currentButtonText || !previousTerm) return;
    if (!currentButtonText.includes(previousTerm)) return;

    searchForTextElement.innerText = currentButtonText.replace(
      previousTerm,
      newTerm
    );
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    const allVisibleElements = Array.from(
      this.querySelectorAll(
        'li[role="option"], button.predictive-search__item[role="option"]'
      )
    ).filter((element) => element.offsetParent !== null);

    if (!allVisibleElements.length) return;

    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (
      selectedElementIndex === -1 &&
      i < allVisibleElements.length
    ) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }

      i++;
    }

    if (this.statusElement) {
      this.statusElement.textContent = '';
    }

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', 'true');

    if (selectedElement) {
      selectedElement.setAttribute('aria-selected', 'false');
    }

    this.input.setAttribute(
      'aria-activedescendant',
      activeElement.id || ''
    );
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, [aria-selected="true"] button'
    );

    if (selectedOption) {
      selectedOption.click();
    }
  }

  /*
   * FAQ-Antwort direkt innerhalb der Predictive Search
   * ein- und ausklappen. Beim ersten Öffnen wird die
   * aktuell formatierte Antwort direkt von der FAQ-Seite geladen.
   */
  async onFaqClick(event) {
    const faqButton = event.target.closest(
      '[data-predictive-search-faq-toggle]'
    );

    if (!faqButton || !this.contains(faqButton)) return;

    event.preventDefault();

    const currentFaqItem = faqButton.closest(
      '.predictive-search__list-item--faq'
    );

    const answerId = faqButton.getAttribute('aria-controls');
    const answerElement = answerId
      ? this.querySelector(`#${CSS.escape(answerId)}`)
      : null;

    if (!currentFaqItem || !answerElement) return;

    const isExpanded =
      faqButton.getAttribute('aria-expanded') === 'true';

    /* Andere geöffnete FAQ-Antworten schließen. */
    this.querySelectorAll(
      '[data-predictive-search-faq-toggle][aria-expanded="true"]'
    ).forEach((openButton) => {
      if (openButton === faqButton) return;

      openButton.setAttribute('aria-expanded', 'false');

      const openAnswerId = openButton.getAttribute('aria-controls');
      const openAnswer = openAnswerId
        ? this.querySelector(`#${CSS.escape(openAnswerId)}`)
        : null;

      if (openAnswer) openAnswer.hidden = true;

      openButton
        .closest('.predictive-search__list-item--faq')
        ?.classList.remove('is-open');
    });

    faqButton.setAttribute(
      'aria-expanded',
      isExpanded ? 'false' : 'true'
    );

    answerElement.hidden = isExpanded;
    currentFaqItem.classList.toggle('is-open', !isExpanded);

    if (isExpanded) return;

    const answerContent = answerElement.querySelector(
      '[data-predictive-search-faq-answer-content]'
    );

    if (
      answerContent &&
      answerContent.getAttribute('data-faq-loaded') !== 'true'
    ) {
      const question = faqButton.getAttribute('data-faq-question') || '';
      const fallbackHtml = answerContent.innerHTML;

      answerContent.setAttribute('aria-busy', 'true');
      answerContent.innerHTML = '<p>Antwort wird geladen …</p>';

      try {
        const formattedAnswerHtml = await this.getFormattedFaqAnswerHtml(
          question
        );

        answerContent.innerHTML = formattedAnswerHtml || fallbackHtml;
        answerContent.setAttribute('data-faq-loaded', 'true');
      } catch (error) {
        console.error('FAQ-Antwort konnte nicht geladen werden:', error);
        answerContent.innerHTML = fallbackHtml;
      } finally {
        answerContent.removeAttribute('aria-busy');
      }
    }

    window.requestAnimationFrame(() => {
      currentFaqItem.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    });
  }

  /*
   * Vollständige FAQ-Seite nur einmal laden und danach
   * für weitere geöffnete Antworten wiederverwenden.
   */
  getFaqPageDocument() {
    if (!this.faqPageDocumentPromise) {
      this.faqPageDocumentPromise = fetch('/pages/avada-faqs', {
        credentials: 'same-origin',
        headers: {
          Accept: 'text/html',
        },
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`FAQ-Seite HTTP-Fehler: ${response.status}`);
          }

          return response.text();
        })
        .then((html) =>
          new DOMParser().parseFromString(html, 'text/html')
        )
        .catch((error) => {
          this.faqPageDocumentPromise = null;
          throw error;
        });
    }

    return this.faqPageDocumentPromise;
  }

  async getFormattedFaqAnswerHtml(question) {
    const faqDocument = await this.getFaqPageDocument();
    const normalizedQuestion = this.normalizeQuery(question);

    const faqItems = Array.from(
      faqDocument.querySelectorAll('.ms-faq-unified__faq-item')
    );

    const matchingItem = faqItems.find((item) => {
      const questionElement = item.querySelector(
        '.ms-faq-unified__faq-question-text'
      );

      return (
        this.normalizeQuery(questionElement?.textContent || '') ===
        normalizedQuestion
      );
    });

    const answerElement = matchingItem?.querySelector(
      '.ms-faq-unified__faq-answer.rte'
    );

    if (!answerElement) return '';

    return this.sanitizeFaqAnswerHtml(answerElement.innerHTML);
  }

  /*
   * Formatierungen und Links der eigenen FAQ-Seite bleiben erhalten.
   * Unsichere Elemente, Event-Handler und javascript:-Links werden entfernt.
   */
  sanitizeFaqAnswerHtml(html) {
    const template = document.createElement('template');
    template.innerHTML = String(html || '');

    template.content
      .querySelectorAll('script, style, iframe, object, embed, form, input, button')
      .forEach((element) => element.remove());

    template.content.querySelectorAll('*').forEach((element) => {
      Array.from(element.attributes).forEach((attribute) => {
        const attributeName = attribute.name.toLowerCase();
        const attributeValue = attribute.value.trim();

        if (
          attributeName.startsWith('on') ||
          attributeName === 'style' ||
          attributeName === 'id'
        ) {
          element.removeAttribute(attribute.name);
        }

        if (
          (attributeName === 'href' || attributeName === 'src') &&
          /^javascript:/i.test(attributeValue)
        ) {
          element.removeAttribute(attribute.name);
        }
      });

      if (
        element.tagName === 'A' &&
        element.getAttribute('target') === '_blank'
      ) {
        element.setAttribute('rel', 'noopener noreferrer');
      }
    });

    return template.innerHTML;
  }

  getSearchResults(searchTerm) {
    const normalizedTerm = this.normalizeQuery(searchTerm);
    const shopifySearchTerm = this.getShopifySearchTerm(normalizedTerm);
    const queryKey = normalizedTerm
      .replace(/\s+/g, '-')
      .toLowerCase();

    if (!normalizedTerm.length) {
      this.close(true);
      return;
    }

    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      if (this.normalizeQuery(this.getQuery()) !== normalizedTerm) return;

      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    this.abortController.abort();
    this.abortController = new AbortController();

    /*
     * Shopify Predictive Search:
     * Produkte, Kategorien, Seiten und Artikel durchsuchen.
     */
    const predictiveUrl =
      `${routes.predictive_search_url}` +
      `?q=${encodeURIComponent(shopifySearchTerm)}` +
      `&resources[type]=product,collection,page,article` +
      `&resources[limit]=8` +
      `&resources[limit_scope]=each` +
      `&resources[options][fields]=title,body,tag,product_type,vendor` +
      `&resources[options][unavailable_products]=last` +
      `&section_id=predictive-search`;

    fetch(predictiveUrl, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          const error = new Error(
            `Predictive Search HTTP-Fehler: ${response.status}`
          );

          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        if (this.normalizeQuery(this.getQuery()) !== normalizedTerm) {
          return;
        }

        let resultsMarkup =
          new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('#shopify-section-predictive-search')
            ?.innerHTML || '';

        /*
         * FAQ-Ergebnisse aus dem lokalen machsport-Index ergänzen.
         */
        resultsMarkup = this.addFaqResultsToMarkup(
          resultsMarkup,
          searchTerm
        );

        this.syncCacheAndRender(queryKey, resultsMarkup);
      })
      .catch((error) => {
        if (
          error?.name === 'AbortError' ||
          error?.code === 20
        ) {
          return;
        }

        console.error(
          'Fehler bei der machsport Predictive Search:',
          error
        );

        this.close();
      });
  }

  normalizeQuery(term) {
    let q = String(term || '')
      .trim()
      .toLowerCase();

    /*
     * Diakritische Zeichen entfernen, damit beispielsweise
     * gültig, gultig und gueltig vergleichbar werden.
     */
    q = q
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

    q = q
      .replace(/ä/g, 'ae')
      .replace(/ö/g, 'oe')
      .replace(/ü/g, 'ue')
      .replace(/ß/g, 'ss');

    q = q
      .replace(/[^\p{L}\p{N}\s-]/gu, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    const synonyms = new Map([
      ['tshirt', 't-shirt'],
      ['tee', 't-shirt'],
      ['pulli', 'pullover'],
      ['hoodie', 'kapuzenpullover'],
    ]);

    q = q
      .split(' ')
      .map((word) => synonyms.get(word) || word)
      .join(' ');

    return q;
  }

  /*
   * Shopify-Such-Aliase:
   * Produkte mit Sonderzeichen im Titel besser finden.
   */
  getShopifySearchTerm(normalizedTerm) {
    let q = normalizedTerm || '';

    const aliases = new Map([
      ['garmin fenix', 'garmin fēnix'],
      ['fenix', 'fēnix'],
    ]);

    aliases.forEach((replacement, search) => {
      const escapedSearch = search.replace(
        /[.*+?^${}()|[\]\\]/g,
        '\\$&'
      );

      const regex = new RegExp(
        `(^|\\s)${escapedSearch}(?=\\s|$)`,
        'gi'
      );

      q = q.replace(regex, `$1${replacement}`);
    });

    return q;
  }

  /*
   * Lokalen FAQ-Index durchsuchen.
   */
  getFaqResults(searchTerm, limit = 5) {
    const faqSource = window.MachsportFaqSearch;

    if (
      !faqSource ||
      !Array.isArray(faqSource.index) ||
      typeof faqSource.normalize !== 'function'
    ) {
      return [];
    }

    const normalizedQuery = faqSource.normalize(searchTerm);

    if (normalizedQuery.length < 2) {
      return [];
    }

    const stopWords = new Set([
      'aber',
      'alle',
      'als',
      'am',
      'an',
      'auch',
      'auf',
      'aus',
      'bei',
      'bin',
      'bis',
      'das',
      'dass',
      'dein',
      'deine',
      'dem',
      'den',
      'der',
      'des',
      'die',
      'du',
      'ein',
      'eine',
      'einer',
      'einem',
      'einen',
      'es',
      'für',
      'fuer',
      'hat',
      'haben',
      'ich',
      'im',
      'in',
      'ist',
      'kann',
      'kannst',
      'mein',
      'meine',
      'mit',
      'muss',
      'oder',
      'sind',
      'so',
      'und',
      'vom',
      'von',
      'wann',
      'warum',
      'was',
      'welche',
      'welcher',
      'welches',
      'wie',
      'wird',
      'wo',
      'zu',
      'zum',
      'zur'
    ]);

    const queryWords = normalizedQuery
      .split(' ')
      .filter(Boolean);

    const importantWords = queryWords.filter(
      (word) => word.length >= 2 && !stopWords.has(word)
    );

    const effectiveWords =
      importantWords.length > 0
        ? importantWords
        : queryWords;

    const scoredResults = faqSource.index
      .map((entry) => {
        const normalizedQuestion = faqSource.normalize(
          entry.question
        );

        const normalizedCategory = faqSource.normalize(
          entry.category
        );

        const normalizedAnswer = faqSource.normalize(
          entry.answer
        );

        const searchableText =
          entry.searchText ||
          [
            normalizedCategory,
            normalizedQuestion,
            normalizedAnswer,
            ...(Array.isArray(entry.keywords)
              ? entry.keywords.map((keyword) =>
                  faqSource.normalize(keyword)
                )
              : [])
          ].join(' ');

        const questionWords = normalizedQuestion.split(' ');
        const categoryWords = normalizedCategory.split(' ');

        let score = 0;
        let matchedWords = 0;

        /*
         * Sehr starke Treffer:
         * Exakte Frage oder vollständige Suchphrase.
         */
        if (normalizedQuestion === normalizedQuery) {
          score += 200;
        }

        if (normalizedQuestion.includes(normalizedQuery)) {
          score += 100;
        }

        if (normalizedQuery.includes(normalizedQuestion)) {
          score += 80;
        }

        if (searchableText.includes(normalizedQuery)) {
          score += 45;
        }

        /*
         * Einzelne wichtige Suchbegriffe gewichten.
         */
        effectiveWords.forEach((word) => {
          let wordMatched = false;

          if (questionWords.includes(word)) {
            score += 24;
            wordMatched = true;
          } else if (
            questionWords.some(
              (questionWord) =>
                questionWord.startsWith(word) ||
                word.startsWith(questionWord)
            )
          ) {
            score += 16;
            wordMatched = true;
          }

          if (categoryWords.includes(word)) {
            score += 10;
            wordMatched = true;
          }

          if (normalizedAnswer.includes(word)) {
            score += 5;
            wordMatched = true;
          }

          if (searchableText.includes(word)) {
            score += 4;
            wordMatched = true;
          }

          if (wordMatched) {
            matchedWords += 1;
          }
        });

        /*
         * Bonus, wenn alle wichtigen Begriffe gefunden wurden.
         */
        if (
          effectiveWords.length > 0 &&
          matchedWords === effectiveWords.length
        ) {
          score += 40;
        }

        /*
         * Anteil der gefundenen Begriffe berücksichtigen.
         */
        if (effectiveWords.length > 0) {
          score += Math.round(
            (matchedWords / effectiveWords.length) * 20
          );
        }

        return {
          entry,
          score,
          matchedWords,
        };
      })
      .filter((result) => {
        if (result.score < 18) return false;

        if (
          effectiveWords.length >= 2 &&
          result.matchedWords === 0
        ) {
          return false;
        }

        return true;
      })
      .sort((a, b) => {
        if (b.score !== a.score) {
          return b.score - a.score;
        }

        return a.entry.question.localeCompare(
          b.entry.question,
          'de'
        );
      });

    /*
     * Doppelte Fragen vermeiden.
     */
    const uniqueResults = [];
    const shownQuestions = new Set();

    for (const result of scoredResults) {
      const questionKey = faqSource.normalize(
        result.entry.question
      );

      if (shownQuestions.has(questionKey)) continue;

      shownQuestions.add(questionKey);
      uniqueResults.push(result.entry);

      if (uniqueResults.length >= limit) break;
    }

    return uniqueResults;
  }

  /*
   * Shopify-Ergebnismarkup um einen FAQ-Block erweitern.
   */
  addFaqResultsToMarkup(resultsMarkup, searchTerm) {
    const faqResults = this.getFaqResults(searchTerm, 5);

    if (!faqResults.length) {
      return resultsMarkup;
    }

    const documentFragment = new DOMParser().parseFromString(
      `<div id="machsport-predictive-search-document">${resultsMarkup}</div>`,
      'text/html'
    );

    const root = documentFragment.querySelector(
      '#machsport-predictive-search-document'
    );

    if (!root) {
      return resultsMarkup;
    }

    let resultsWrapper = root.querySelector(
      '#predictive-search-results-groups-wrapper'
    );

    if (!resultsWrapper) {
      const resultsContainer = root.querySelector(
        '#predictive-search-results'
      );

      if (!resultsContainer) {
        return resultsMarkup;
      }

      resultsWrapper = documentFragment.createElement('div');
      resultsWrapper.id =
        'predictive-search-results-groups-wrapper';

      resultsWrapper.className =
        'predictive-search__results-wrapper-two-columns';

      resultsContainer.prepend(resultsWrapper);
    }

    let rightColumn = resultsWrapper.querySelector(
      '.predictive-search__column--right'
    );

    if (!rightColumn) {
      rightColumn = documentFragment.createElement('div');

      rightColumn.className =
        'predictive-search__column predictive-search__column--right';

      resultsWrapper.appendChild(rightColumn);
    }

    const faqGroup = documentFragment.createElement('div');

    faqGroup.className =
      'predictive-search__result-group predictive-search__result-group--faq';

    faqGroup.setAttribute(
      'data-predictive-search-faq-group',
      ''
    );

    const headingId = 'predictive-search-faq-heading';

    const faqItems = faqResults
      .map((faq, index) => {
        const optionId =
          `predictive-search-option-faq-${index + 1}`;

        const question = this.escapeHtml(faq.question);
        const answer = this.escapeHtml(faq.answer);
        const category = this.escapeHtml(faq.category);
        const url = this.escapeHtmlAttribute(faq.url);

        const answerId =
          `predictive-search-faq-answer-${index + 1}`;

        return `
          <li
            id="${optionId}"
            class="predictive-search__list-item predictive-search__list-item--faq"
            role="option"
            aria-selected="false"
          >
            <button
              type="button"
              class="predictive-search__item predictive-search__item--faq link link--text"
              tabindex="-1"
              aria-expanded="false"
              aria-controls="${answerId}"
              data-predictive-search-faq-toggle
              data-faq-question="${this.escapeHtmlAttribute(faq.question)}"
            >
              <span class="predictive-search__faq-content">
                <span class="predictive-search__faq-category">
                  ${category}
                </span>

                <span class="predictive-search__item-heading predictive-search__faq-question h5">
                  ${question}
                </span>

                <span
                  class="predictive-search__faq-toggle-icon"
                  aria-hidden="true"
                >
                  +
                </span>
              </span>
            </button>

            <div
              id="${answerId}"
              class="predictive-search__faq-answer-panel"
              hidden
            >
              <div
                class="predictive-search__faq-answer rte"
                data-predictive-search-faq-answer-content
                data-faq-loaded="false"
              >
                <p>${answer}</p>
              </div>

              <a
                href="${url}"
                class="predictive-search__faq-page-link link"
              >
                Zur vollständigen FAQ-Seite
              </a>
            </div>
          </li>
        `;
      })
      .join('');

    faqGroup.innerHTML = `
      <h2
        id="${headingId}"
        class="predictive-search__heading predictive-search__heading--faq"
      >
        Häufige Fragen
      </h2>

      <ul
        class="predictive-search__results-list predictive-search__faq-list list-unstyled"
        role="group"
        aria-labelledby="${headingId}"
      >
        ${faqItems}
      </ul>
    `;

    /*
     * FAQ vor „Ratgeber & Infos“ einfügen.
     */
    const contentHeading = Array.from(
      rightColumn.querySelectorAll(
        '.predictive-search__heading'
      )
    ).find((heading) =>
      heading.textContent
        .trim()
        .toLowerCase()
        .includes('ratgeber')
    );

    const contentGroup = contentHeading?.closest(
      '.predictive-search__result-group'
    );

    if (contentGroup) {
      rightColumn.insertBefore(faqGroup, contentGroup);
    } else {
      rightColumn.appendChild(faqGroup);
    }

    /*
     * Anzahl der Ergebnisse für Screenreader ergänzen.
     */
    const liveRegionCount = root.querySelector(
      '[data-predictive-search-live-region-count-value]'
    );

    if (liveRegionCount) {
      const existingText =
        liveRegionCount.textContent.trim();

      const faqText =
        `${faqResults.length} passende ` +
        `${faqResults.length === 1 ? 'FAQ' : 'FAQ'}`;

      liveRegionCount.textContent = existingText
        ? `${existingText}, ${faqText}`
        : faqText;
    }

    return root.innerHTML;
  }

  escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  escapeHtmlAttribute(value) {
    return this.escapeHtml(value)
      .replace(/`/g, '&#096;');
  }

  syncCacheAndRender(queryKey, markup) {
    this.allPredictiveSearchInstances.forEach((instance) => {
      instance.cachedResults[queryKey] = markup;
    });

    this.renderSearchResults(markup);
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement ||
      this.querySelector('.predictive-search-status');

    this.loadingText =
      this.loadingText ||
      this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;

    this.statusElement.setAttribute(
      'aria-hidden',
      'false'
    );

    this.statusElement.textContent =
      statusText || '';

    window.setTimeout(() => {
      this.statusElement.setAttribute(
        'aria-hidden',
        'true'
      );
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML =
      resultsMarkup;

    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');

    const countElement = this.querySelector(
      '[data-predictive-search-live-region-count-value]'
    );

    if (countElement) {
      this.setLiveRegionText(
        countElement.textContent
      );
    } else {
      this.setLiveRegionText('');
    }
  }

  getResultsMaxHeight() {
    const headerBottom =
      document
        .querySelector('.section-header')
        ?.getBoundingClientRect()
        .bottom || 0;

    this.resultsMaxHeight =
      window.innerHeight - headerBottom;

    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      this.resultsMaxHeight ||
      `${this.getResultsMaxHeight()}px`;

    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.searchTerm = '';
      this.removeAttribute('results');

      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.innerHTML = '';
      }
    }

    const selected = this.querySelector(
      '[aria-selected="true"]'
    );

    if (selected) {
      selected.setAttribute(
        'aria-selected',
        'false'
      );
    }

    this.input.setAttribute(
      'aria-activedescendant',
      ''
    );

    this.removeAttribute('loading');
    this.removeAttribute('open');

    this.input.setAttribute(
      'aria-expanded',
      'false'
    );

    this.resultsMaxHeight = false;

    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.removeAttribute(
        'style'
      );
    }
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define(
    'predictive-search',
    PredictiveSearch
  );
}